import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { GlobalQuestionService } from '../../../services/global-question.service';
import {
  CompareDetailGroup,
  CompareItemGroup,
  CompareRow,
  alignDetailGroups,
  alignItemGroups,
  alignTopRows,
  insurerRatingRows,
  overallRating,
  starPercents
} from './quote-compare.util';

/**
 * The printable Compare page the pdf-service renders (route everything.quote-compare-pdf):
 * every part of the Compare modal laid out open, for the products in ?product_ids. It runs in
 * a fresh browser, so it reads getQuoteCompare and getQuoteCompareDetail itself instead of
 * relying on the quote page's state, and it never calls getQuotePageData (that runs the calc).
 */
@Component({
  selector: 'app-quote-compare-pdf',
  templateUrl: './quote-compare-pdf.component.html',
  styleUrls: ['./quote-compare-pdf.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class QuoteComparePdfComponent implements OnInit {
  loading = true;
  error = false;
  productIds: number[] = [];
  /** The product header row per column. */
  columns: any[] = [];
  frequency = '';
  validUntil = '';

  topFeatures: CompareRow[] = [];
  topIncluded: CompareRow[] = [];
  topNotIncluded: CompareRow[] = [];
  insurerRows: { rating_type: string; cells: any[] }[] = [];
  itemGroups: CompareItemGroup[] = [];
  benefitGroups: CompareDetailGroup[] = [];
  coveredGroups: CompareDetailGroup[] = [];
  notCoveredGroups: CompareDetailGroup[] = [];

  private detail: any[] = [];
  private sessionId = '';
  private groupId = '';

  constructor(private route: ActivatedRoute, private questionService: GlobalQuestionService) {}

  ngOnInit(): void {
    const query = this.route.snapshot.queryParamMap;
    this.sessionId = query.get('session_id') ?? '';
    this.groupId = query.get('product_group_id') ?? '';
    this.productIds = (query.get('product_ids') ?? '').split(',').map(id => parseInt(id, 10)).filter(id => id > 0);
    if (!this.sessionId || !this.groupId || !this.productIds.length) {
      this.loading = false;
      this.error = true;
      this.questionService.spinner(false);
      this.questionService.failHeadlessPDF('compare page needs session_id, product_group_id and product_ids');
      return;
    }

    this.questionService.spinner(true);
    const top = new Map<string, string>();
    top.set('session_id', this.sessionId);
    top.set('product_group_id', this.groupId);
    const detail = new Map<string, string>();
    detail.set('session_id', this.sessionId);
    detail.set('product_ids', this.productIds.join(','));
    forkJoin([
      this.questionService.runCommandNew('getQuoteCompare', top),
      this.questionService.runCommandNew('getQuoteCompareDetail', detail)
    ]).subscribe({
      next: ([topRows, detailRows]: [any[], any[]]) => {
        this.build(topRows ?? [], detailRows ?? []);
        this.loading = false;
        // Render first, then hand over: in a headless capture generatePDF() flips the ready flag the
        // pdf-service waits on; opened by hand with pdf=true it requests the PDF for this same page.
        setTimeout(() => this.finish(), 300);
      },
      error: () => {
        this.loading = false;
        this.error = true;
        this.questionService.spinner(false);
        this.questionService.failHeadlessPDF('compare data could not be loaded');
      }
    });
  }

  private build(topRows: any[], detail: any[]): void {
    this.detail = detail;
    const ids = this.productIds;
    this.columns = ids.map(id => detail.find(r => r.product_id === id && r.entry_type === 'product') ?? null);
    this.frequency = this.columns.find(column => column)?.description ?? '';
    this.validUntil = detail.find(r => r.entry_type === 'quote')?.value ?? '';
    this.topFeatures = alignTopRows(topRows, 'feature', ids);
    this.topIncluded = alignTopRows(topRows, 'included', ids);
    this.topNotIncluded = alignTopRows(topRows, 'not_included', ids);
    this.insurerRows = insurerRatingRows(detail, ids);
    this.itemGroups = alignItemGroups(detail, ids, this.frequency);
    this.benefitGroups = alignDetailGroups(detail, 'benefit', ids);
    this.coveredGroups = alignDetailGroups(detail, 'covered', ids);
    this.notCoveredGroups = alignDetailGroups(detail, 'not_covered', ids);
  }

  private finish(): void {
    if (this.route.snapshot.queryParamMap.get('pdf') === 'true') {
      this.questionService.generatePDF({
        sessionId: this.sessionId,
        extraParams: { product_group_id: this.groupId, product_ids: this.productIds.join(',') }
      }).subscribe({ error: () => this.questionService.spinner(false) });
    } else {
      this.questionService.spinner(false);
    }
  }

  stars(column: number): string[] {
    return starPercents(this.detail, this.productIds[column]);
  }

  overall(column: number): number {
    return overallRating(this.detail, this.productIds[column]);
  }

  /** The missing-section guards: a section only prints when there is something to print. */
  get hasDetail(): boolean {
    return !!(this.itemGroups.length || this.insurerRows.length || this.benefitGroups.length || this.coveredGroups.length || this.notCoveredGroups.length);
  }
}
